import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import classNames from 'classnames';

import UploadChooseFile from './UploadChooseFile';
import { errorMessage, IN_PROGRESS_STATUSES, POLL_INTERVAL_MS } from './uploadFormat';
import UploadImpactReview from './UploadImpactReview';
import UploadStatus from './UploadStatus';
import UploadValidationReport from './UploadValidationReport';

import { idempotencyHeader } from 'Helpers/apiUtils';
import { apiClient } from 'Helpers/network';

import Button from 'CommonComponents/Button';

const STEP_CHOOSE = 'choose';
const STEP_REPORT = 'report';
const STEP_IMPACT = 'impact';
const STEP_STATUS = 'status';

const STEPS = [
    { key: STEP_CHOOSE, label: () => $t('Choose file') },
    { key: STEP_REPORT, label: () => $t('Validation report') },
    { key: STEP_IMPACT, label: () => $t('Confirm impact') },
    { key: STEP_STATUS, label: () => $t('Status') },
];

const withBatch = (url, batchId) => url.replace('BATCH_ID', batchId);

function StepIndicator({ current }) {
    const currentIndex = STEPS.findIndex((step) => step.key === current);

    return (
        <ol className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-2 tw-text-sm">
            {STEPS.map((step, index) => (
                <li key={step.key} className="tw-flex tw-items-center tw-gap-x-2" aria-current={index === currentIndex ? 'step' : undefined}>
                    <span
                        className={classNames('tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-text-xs tw-font-semibold', {
                            'tw-bg-blue tw-text-white': index === currentIndex,
                            'tw-bg-green-25 tw-text-dark-green': index < currentIndex,
                            'tw-bg-charcoal-15 tw-text-charcoal-75': index > currentIndex,
                        })}
                    >
                        {index + 1}
                    </span>
                    <span className={classNames(index === currentIndex ? 'tw-font-semibold tw-text-charcoal' : 'tw-text-charcoal-75')}>{step.label()}</span>
                    {index < STEPS.length - 1 && <span className="tw-h-px tw-w-6 tw-bg-charcoal-25" aria-hidden="true" />}
                </li>
            ))}
        </ol>
    );
}

/**
 * The Actuals spreadsheet upload: choose a file (validated at once, nothing
 * written), review the validation report, confirm the impact, then follow the
 * write, which runs in the background.
 */
export default function ActualsUploadPage({ validateUrl, statusUrl, confirmUrl, issuesUrl, sampleUrl, actualsUrl, historyUrl, templateVersion }) {
    const [step, setStep] = useState(STEP_CHOOSE);
    const [batch, setBatch] = useState(null);
    const [fileName, setFileName] = useState('');
    const [validating, setValidating] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    // A 409 on confirm: the project changed (or is being written) since the file
    // was checked, so the impact shown is out of date and the file must be checked again.
    const [conflict, setConflict] = useState(null);

    const reset = useCallback(() => {
        setStep(STEP_CHOOSE);
        setBatch(null);
        setFileName('');
        setError(null);
        setConflict(null);
        setValidating(false);
        setSubmitting(false);
    }, []);

    const validateFile = (file) => {
        const data = new FormData();
        data.append('file', file);

        setFileName(file.name);
        setError(null);
        setValidating(true);

        apiClient
            .post(validateUrl, data, { headers: { ...idempotencyHeader() }, expectedErrorStatuses: [422] })
            .then((response) => {
                setBatch(response.data);
                setStep(STEP_REPORT);
            })
            .catch((requestError) => setError(errorMessage(requestError)))
            .finally(() => setValidating(false));
    };

    const confirmUpload = () => {
        setError(null);
        setConflict(null);
        setSubmitting(true);

        apiClient
            .post(withBatch(confirmUrl, batch.batch_id), {}, { headers: { ...idempotencyHeader() }, expectedErrorStatuses: [409, 422] })
            .then((response) => {
                setBatch((previous) => ({ ...previous, status: response.data.status }));
                setStep(STEP_STATUS);
            })
            .catch((requestError) => {
                if (requestError?.response?.status === 409) {
                    setConflict(errorMessage(requestError));
                } else {
                    setError(errorMessage(requestError));
                }
            })
            .finally(() => setSubmitting(false));
    };

    const batchId = batch?.batch_id;
    const polling = step === STEP_STATUS && !!batch && IN_PROGRESS_STATUSES.includes(batch.status);

    useEffect(() => {
        if (!polling) {
            return undefined;
        }

        let cancelled = false;
        const poll = () => {
            apiClient
                .get(withBatch(statusUrl, batchId))
                .then((response) => {
                    if (!cancelled) {
                        setBatch(response.data);
                    }
                })
                // A missed poll is retried on the next tick; a lasting failure is
                // reported by the client and the page keeps its last known state.
                .catch(() => {});
        };

        poll();
        const timer = window.setInterval(poll, POLL_INTERVAL_MS);

        return () => {
            cancelled = true;
            window.clearInterval(timer);
        };
    }, [polling, batchId, statusUrl]);

    return (
        <div className="tw-flex tw-flex-col tw-gap-y-4 tw-pb-6">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                <div>
                    <h2 className="tw-text-lg tw-font-semibold tw-text-charcoal">{$t('Upload actuals from Excel')}</h2>
                    <p className="tw-mt-1 tw-text-sm tw-text-charcoal-75">{$t('Validate the file, confirm what it will change, then write it.')}</p>
                </div>
                <Button as="a" small variant="secondary" href={historyUrl}>
                    {$t('Upload history')}
                </Button>
            </div>

            <StepIndicator current={step} />

            {step === STEP_CHOOSE && (
                <UploadChooseFile
                    sampleUrl={sampleUrl}
                    templateVersion={templateVersion}
                    validating={validating}
                    fileName={fileName}
                    error={error}
                    onFileChosen={validateFile}
                />
            )}

            {step === STEP_REPORT && batch && (
                <UploadValidationReport
                    batch={batch}
                    issuesUrl={withBatch(issuesUrl, batch.batch_id)}
                    onContinue={() => {
                        setError(null);
                        setStep(STEP_IMPACT);
                    }}
                    onCancel={reset}
                />
            )}

            {step === STEP_IMPACT && batch && (
                <UploadImpactReview
                    batch={batch}
                    submitting={submitting}
                    error={error}
                    conflict={conflict}
                    onCheckAgain={reset}
                    onBack={() => {
                        setError(null);
                        setStep(STEP_REPORT);
                    }}
                    onCancel={reset}
                    onConfirm={confirmUpload}
                />
            )}

            {step === STEP_STATUS && batch && <UploadStatus batch={batch} historyUrl={historyUrl} actualsUrl={actualsUrl} onStartAgain={reset} />}
        </div>
    );
}

if (document.getElementById('actualsUploadPage')) {
    const rootEl = document.getElementById('actualsUploadPage');

    ReactDOM.render(
        <App>
            <ActualsUploadPage
                validateUrl={rootEl.getAttribute('data-validate-url')}
                statusUrl={rootEl.getAttribute('data-status-url')}
                confirmUrl={rootEl.getAttribute('data-confirm-url')}
                issuesUrl={rootEl.getAttribute('data-issues-url')}
                sampleUrl={rootEl.getAttribute('data-sample-url')}
                actualsUrl={rootEl.getAttribute('data-actuals-url')}
                historyUrl={rootEl.getAttribute('data-history-url')}
                templateVersion={Number(rootEl.getAttribute('data-template-version'))}
            />
        </App>,
        rootEl,
    );
}
